export type PathSeg = string | number;

export type CompositionRowKind = "layer" | "group" | "fill" | "stroke" | "shape" | "other";

export interface CompositionRow {
  path: PathSeg[];
  depth: number;
  kind: CompositionRowKind;
  title: string;
  detail?: string;
}

const KS_LABELS: Record<string, string> = {
  a: "Anchor",
  p: "Position",
  s: "Scale",
  r: "Rotation",
  o: "Opacity",
  sk: "Skew",
  sa: "Skew axis",
};

export function getAtPath(root: unknown, path: PathSeg[]): unknown {
  let cur: unknown = root;
  for (const seg of path) {
    if (cur === null || cur === undefined) {
      return undefined;
    }
    if (typeof seg === "number") {
      if (!Array.isArray(cur)) {
        return undefined;
      }
      cur = cur[seg];
    } else if (typeof cur === "object" && cur !== null && seg in cur) {
      cur = (cur as Record<string, unknown>)[seg];
    } else {
      return undefined;
    }
  }
  return cur;
}

function descendSetBranch(cur: unknown, seg: PathSeg, next: PathSeg): unknown | null {
  if (typeof seg === "number") {
    if (!Array.isArray(cur)) {
      return null;
    }
    if (cur[seg] === undefined) {
      cur[seg] = typeof next === "number" ? [] : {};
    }
    return cur[seg];
  }
  const obj = cur as Record<string, unknown>;
  if (obj[seg] === undefined || obj[seg] === null) {
    obj[seg] = typeof next === "number" ? [] : {};
  }
  return obj[seg];
}

function assignLastSegment(cur: unknown, last: PathSeg, value: unknown): void {
  if (typeof last === "number") {
    if (!Array.isArray(cur)) {
      return;
    }
    cur[last] = value as never;
    return;
  }
  (cur as Record<string, unknown>)[last] = value as unknown;
}

export function setAtPath(root: unknown, path: PathSeg[], value: unknown): unknown {
  if (path.length === 0) {
    return value;
  }
  const clone: unknown = JSON.parse(JSON.stringify(root));
  let cur: unknown = clone;
  for (let i = 0; i < path.length - 1; i++) {
    const seg = path[i];
    const next = path[i + 1];
    if (seg === undefined || next === undefined) {
      return clone;
    }
    const step = descendSetBranch(cur, seg, next);
    if (step === null) {
      return clone;
    }
    cur = step;
  }
  const last = path[path.length - 1];
  if (last === undefined) {
    return clone;
  }
  assignLastSegment(cur, last, value);
  return clone;
}

export function pathKey(path: PathSeg[]): string {
  return JSON.stringify(path);
}

export function parsePathKey(key: string): PathSeg[] | null {
  try {
    const v = JSON.parse(key) as unknown;
    if (!Array.isArray(v)) {
      return null;
    }
    return v as PathSeg[];
  } catch {
    return null;
  }
}

interface RootLike {
  layers?: unknown;
}

interface LayerLike {
  nm?: string;
  ty?: number;
  shapes?: unknown[];
}

interface ShapeItemLike {
  ty?: string;
  nm?: string;
  it?: unknown[];
}

export function buildCompositionRows(animationData: unknown): CompositionRow[] {
  const rows: CompositionRow[] = [];
  if (!animationData || typeof animationData !== "object") {
    return rows;
  }
  const root = animationData as RootLike;
  const layers = root.layers;
  if (!Array.isArray(layers)) {
    return rows;
  }
  layers.forEach((layer, layerIndex) => {
    walkLayer(layer, layerIndex, rows);
  });
  return rows;
}

function walkLayer(layer: unknown, layerIndex: number, rows: CompositionRow[]): void {
  const L = layer as LayerLike;
  const nm = typeof L.nm === "string" ? L.nm : `Layer ${layerIndex}`;
  const ty = L.ty;
  const tyLabel = typeof ty === "number" ? `ty ${ty}` : String(ty);
  rows.push({
    path: ["layers", layerIndex],
    depth: 0,
    kind: "layer",
    title: nm,
    detail: tyLabel,
  });
  if (ty === 4 && Array.isArray(L.shapes)) {
    walkShapeItems(L.shapes, ["layers", layerIndex, "shapes"], 1, rows);
  }
}

function compositionKindForShapeTy(ty: unknown): CompositionRowKind {
  switch (ty) {
    case "gr":
      return "group";
    case "fl":
      return "fill";
    case "st":
      return "stroke";
    case "rc":
    case "el":
    case "sr":
    case "sh":
    case "tr":
      return "shape";
    default:
      return "other";
  }
}

function walkShapeItems(items: unknown[], basePath: PathSeg[], depth: number, rows: CompositionRow[]): void {
  items.forEach((item, index) => {
    const o = item as ShapeItemLike;
    const ty = o.ty;
    const name = typeof o.nm === "string" ? o.nm : String(ty ?? "item");
    const path = [...basePath, index];
    const kind = compositionKindForShapeTy(ty);
    const detail = kind === "shape" || kind === "other" ? (typeof ty === "string" ? ty : String(ty)) : undefined;
    rows.push({
      path,
      depth,
      kind,
      title: name,
      ...(detail !== undefined ? { detail } : {}),
    });
    if (ty === "gr" && Array.isArray(o.it)) {
      walkShapeItems(o.it, [...path, "it"], depth + 1, rows);
    }
  });
}

export interface KeyframeRow {
  index: number;
  frame: string;
  valueSummary: string;
}

export function summariseAnimatedProperty(prop: unknown): KeyframeRow[] {
  if (!prop || typeof prop !== "object") {
    return [];
  }
  const o = prop as { a?: number; k?: unknown };
  if (o.a === 0) {
    return [{ index: 0, frame: "—", valueSummary: stringifyCompact(o.k) }];
  }
  if (o.a !== 1 || !Array.isArray(o.k)) {
    return [];
  }
  return o.k.map((kf, index) => extractKeyframeRow(kf, index));
}

interface KeyframeLike {
  t?: number;
  s?: unknown;
  e?: unknown;
}

function extractKeyframeRow(kf: unknown, index: number): KeyframeRow {
  if (!kf || typeof kf !== "object") {
    return { index, frame: String(index), valueSummary: stringifyCompact(kf) };
  }
  const r = kf as KeyframeLike;
  const t = r.t;
  const frame = typeof t === "number" ? String(Math.round(t)) : String(index);
  let val: unknown = kf;
  if (r.s !== undefined) {
    val = r.s;
  } else if (r.e !== undefined) {
    val = r.e;
  }
  return { index, frame, valueSummary: stringifyCompact(val) };
}

function stringifyCompact(v: unknown): string {
  try {
    const s = JSON.stringify(v);
    if (s.length > 120) {
      return `${s.slice(0, 117)}…`;
    }
    return s;
  } catch {
    return String(v);
  }
}

export function rgb01ToHex(rgb: readonly number[]): string {
  const [r0 = 0, g0 = 0, b0 = 0] = rgb;
  const clamp = (x: number) => Math.min(255, Math.max(0, Math.round(x * 255)));
  const r = clamp(r0);
  const g = clamp(g0);
  const b = clamp(b0);
  const h = (n: number) => n.toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}

export function hexToRgb01(hex: string): [number, number, number] {
  const n = hex.replace("#", "").slice(0, 6);
  if (n.length !== 6) {
    return [0, 0, 0];
  }
  const r = Number.parseInt(n.slice(0, 2), 16) / 255;
  const g = Number.parseInt(n.slice(2, 4), 16) / 255;
  const b = Number.parseInt(n.slice(4, 6), 16) / 255;
  return [r, g, b];
}

export function readOpacityChannel(oObj: unknown): number | null {
  if (!oObj || typeof oObj !== "object") {
    return null;
  }
  const o = oObj as { a?: number; k?: unknown };
  if (o.a === 0 && typeof o.k === "number") {
    return o.k;
  }
  return null;
}

export function staticOpacityProperty(op: number): { a: 0; k: number } {
  return { a: 0, k: op };
}

export function readStrokeWidth(wObj: unknown): number | null {
  if (!wObj || typeof wObj !== "object") {
    return null;
  }
  const w = wObj as { a?: number; k?: unknown };
  if (w.a === 0 && typeof w.k === "number") {
    return w.k;
  }
  return null;
}

export function staticStrokeWidth(width: number): { a: 0; k: number } {
  return { a: 0, k: width };
}

function rgbTripleFromArray(arr: unknown): [number, number, number] | null {
  if (!Array.isArray(arr) || arr.length < 3) {
    return null;
  }
  const [r, g, b] = arr as number[];
  if (typeof r !== "number" || typeof g !== "number" || typeof b !== "number") {
    return null;
  }
  return [r, g, b];
}

export function normaliseLottieRgbTriple(raw: unknown): [number, number, number] | null {
  const base = rgbTripleFromArray(raw);
  if (!base) {
    return null;
  }
  let [r, g, b] = base;
  const max = Math.max(r, g, b);
  if (max > 1.0001) {
    r /= 255;
    g /= 255;
    b /= 255;
  }
  const clamp = (x: number) => Math.min(1, Math.max(0, x));
  return [clamp(r), clamp(g), clamp(b)];
}

function normaliseFcArraysInSubtree(value: unknown, depth: number): void {
  if (depth > 30 || value === null || typeof value !== "object") {
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      normaliseFcArraysInSubtree(item, depth + 1);
    }
    return;
  }
  const node = value as { fc?: unknown };
  const norm = normaliseLottieRgbTriple(node.fc);
  if (norm) {
    node.fc = [...norm];
  }
  const o = value as Record<string, unknown>;
  for (const key of Object.keys(o)) {
    if (key === "fc") {
      continue;
    }
    normaliseFcArraysInSubtree(o[key], depth + 1);
  }
}

function normaliseTextDocumentFcByteToUnit(layer: Record<string, unknown>): void {
  const t = (layer as { t?: unknown }).t;
  if (!t || typeof t !== "object") {
    return;
  }
  normaliseFcArraysInSubtree(t, 0);
}

interface LayerWithTy {
  ty?: unknown;
}

interface TextStyleSource {
  fc?: unknown;
}

interface LayerTextNav {
  t?: { d?: { k?: unknown } };
}

interface TextDataInner {
  k?: unknown;
}

interface TextDocumentHolder {
  d?: TextDataInner;
}

interface LottieJsonClone {
  layers?: unknown[];
  assets?: unknown[];
}

interface AssetMaybeLayers {
  layers?: unknown[];
}

interface TextDocWrap {
  a?: number;
  k?: unknown;
  s?: unknown;
}

interface KeyframeEntry {
  s?: unknown;
}

interface RGBFillStyle {
  fc?: unknown;
}

interface StaticTextDocK {
  s?: unknown;
  fc?: unknown;
}

export function getLayerTy(layer: unknown): number | undefined {
  if (!layer || typeof layer !== "object") {
    return undefined;
  }
  const ty = (layer as LayerWithTy).ty;
  return typeof ty === "number" ? ty : undefined;
}

function getTextDocumentWrap(L: Record<string, unknown>): TextDocWrap | null {
  const dk = (L as LayerTextNav).t?.d?.k;
  if (dk === undefined || dk === null || typeof dk !== "object") {
    return null;
  }
  return dk as TextDocWrap;
}

function textStyleFromAnimatedWrap(wrap: TextDocWrap): Record<string, unknown> | null {
  if (wrap.a !== 1 || !Array.isArray(wrap.k) || wrap.k.length === 0) {
    return null;
  }
  const k0 = wrap.k[0];
  if (!k0 || typeof k0 !== "object") {
    return null;
  }
  const s = (k0 as KeyframeEntry).s;
  if (!s || typeof s !== "object") {
    return null;
  }
  return s as Record<string, unknown>;
}

function textStyleFromStaticWrap(wrap: TextDocWrap): Record<string, unknown> | null {
  if (wrap.a !== 0) {
    return null;
  }
  const k = wrap.k;
  if (!k || typeof k !== "object") {
    return null;
  }
  const ko = k as StaticTextDocK;
  if (ko.s !== undefined && typeof ko.s === "object") {
    return ko.s as Record<string, unknown>;
  }
  if (Array.isArray(ko.fc)) {
    return ko as Record<string, unknown>;
  }
  return null;
}

function textStyleFromLooseWrap(wrap: TextDocWrap): Record<string, unknown> | null {
  if (wrap.s === undefined || wrap.s === null || typeof wrap.s !== "object") {
    return null;
  }
  return wrap.s as Record<string, unknown>;
}

function getTextStyleSourceForRead(L: Record<string, unknown>): Record<string, unknown> | null {
  const wrap = getTextDocumentWrap(L);
  if (!wrap) {
    return null;
  }
  return textStyleFromAnimatedWrap(wrap) ?? textStyleFromStaticWrap(wrap) ?? textStyleFromLooseWrap(wrap);
}

export function readTextFillRgb01(layer: unknown): [number, number, number] | null {
  if (!layer || typeof layer !== "object") {
    return null;
  }
  const style = getTextStyleSourceForRead(layer as Record<string, unknown>);
  if (!style) {
    return null;
  }
  return normaliseLottieRgbTriple((style as TextStyleSource).fc);
}

function applyAnimatedTextFills(wrap: TextDocWrap, triple: [number, number, number]): boolean {
  if (wrap.a !== 1 || !Array.isArray(wrap.k) || wrap.k.length === 0) {
    return false;
  }
  for (const item of wrap.k) {
    if (item && typeof item === "object") {
      const s = (item as KeyframeEntry).s;
      if (s && typeof s === "object") {
        (s as RGBFillStyle).fc = [...triple];
      }
    }
  }
  return true;
}

function applyStaticTextFill(wrap: TextDocWrap, triple: [number, number, number]): boolean {
  if (wrap.a !== 0) {
    return false;
  }
  const k = wrap.k;
  if (!k || typeof k !== "object") {
    return false;
  }
  const ko = k as StaticTextDocK;
  if (ko.s !== undefined && typeof ko.s === "object") {
    (ko.s as RGBFillStyle).fc = [...triple];
    return true;
  }
  if (Array.isArray(ko.fc)) {
    ko.fc = [...triple];
    return true;
  }
  return false;
}

function applyLooseWrapTextFill(wrap: TextDocWrap, triple: [number, number, number]): boolean {
  if (wrap.s === undefined || wrap.s === null || typeof wrap.s !== "object") {
    return false;
  }
  (wrap.s as RGBFillStyle).fc = [...triple];
  return true;
}

function applyTextFillToLayer(layer: Record<string, unknown>, rgb: [number, number, number]): boolean {
  const wrap = getTextDocumentWrap(layer);
  if (!wrap) {
    return false;
  }
  const triple: [number, number, number] = [rgb[0], rgb[1], rgb[2]];
  if (applyAnimatedTextFills(wrap, triple)) {
    return true;
  }
  if (applyStaticTextFill(wrap, triple)) {
    return true;
  }
  return applyLooseWrapTextFill(wrap, triple);
}

export const DEFAULT_TEXT_FILL_RGB01: [number, number, number] = [1, 1, 1];

function inferTextDocWrapAnimatorFlag(wrap: TextDocWrap): void {
  if (wrap.a !== undefined) {
    return;
  }
  if (Array.isArray(wrap.k)) {
    wrap.a = 1;
    return;
  }
  if (wrap.k !== undefined && wrap.k !== null && typeof wrap.k === "object") {
    wrap.a = 0;
  }
}

function patchAnimatedKeyframesMissingFc(keyframes: unknown[], triple: [number, number, number]): boolean {
  if (keyframes.length === 0) {
    keyframes.push({ s: { fc: [...triple], t: "" } });
    return true;
  }
  let changed = false;
  for (const item of keyframes) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const kf = item as KeyframeEntry & Record<string, unknown>;
    if (!kf.s || typeof kf.s !== "object") {
      kf.s = { fc: [...triple], t: "" };
      changed = true;
      continue;
    }
    const s = kf.s as RGBFillStyle;
    if (normaliseLottieRgbTriple(s.fc) === null) {
      s.fc = [...triple];
      changed = true;
    }
  }
  return changed;
}

function patchStaticTextDocKShape(
  ko: StaticTextDocK & Record<string, unknown>,
  triple: [number, number, number]
): boolean {
  if (ko.s !== undefined && typeof ko.s === "object") {
    const s = ko.s as RGBFillStyle;
    if (normaliseLottieRgbTriple(s.fc) === null) {
      s.fc = [...triple];
    }
    return true;
  }
  if (Array.isArray(ko.fc)) {
    if (normaliseLottieRgbTriple(ko.fc) === null) {
      ko.fc = [...triple];
    }
    return true;
  }
  ko.s = { fc: [...triple], t: "" };
  return true;
}

function patchLooseWrapSurface(style: RGBFillStyle, triple: [number, number, number]): boolean {
  if (normaliseLottieRgbTriple(style.fc) === null) {
    style.fc = [...triple];
  }
  return true;
}

function resetWrapToStaticWhiteDoc(wrap: TextDocWrap, triple: [number, number, number]): void {
  wrap.a = 0;
  wrap.k = { s: { t: "", fc: [...triple] } };
  const surface = wrap as { s?: unknown };
  delete surface.s;
}

function coerceTextDocWrapDefaultFill(wrap: TextDocWrap): boolean {
  inferTextDocWrapAnimatorFlag(wrap);
  const triple = DEFAULT_TEXT_FILL_RGB01;
  if (wrap.a === 1 && Array.isArray(wrap.k)) {
    return patchAnimatedKeyframesMissingFc(wrap.k, triple);
  }
  if (wrap.a === 0 && wrap.k !== undefined && wrap.k !== null && typeof wrap.k === "object" && !Array.isArray(wrap.k)) {
    return patchStaticTextDocKShape(wrap.k as StaticTextDocK & Record<string, unknown>, triple);
  }
  if (wrap.s !== undefined && wrap.s !== null && typeof wrap.s === "object") {
    return patchLooseWrapSurface(wrap.s as RGBFillStyle, triple);
  }
  resetWrapToStaticWhiteDoc(wrap, triple);
  return true;
}

function attachMinimalTextDocument(layer: Record<string, unknown>): boolean {
  const triple = DEFAULT_TEXT_FILL_RGB01;
  const holder = layer as { t?: TextDocumentHolder };
  const tIn = holder.t;
  let t: TextDocumentHolder;
  if (tIn && typeof tIn === "object") {
    t = JSON.parse(JSON.stringify(tIn)) as TextDocumentHolder;
  } else {
    t = {};
  }
  const dIn = t.d;
  let d: TextDataInner;
  if (dIn && typeof dIn === "object") {
    d = JSON.parse(JSON.stringify(dIn)) as TextDataInner;
  } else {
    d = {};
  }
  d.k = {
    a: 0,
    k: {
      s: {
        t: "",
        fc: [...triple],
      },
    },
  };
  t.d = d;
  holder.t = t;
  return true;
}

function textAnimatorHasFillAnimator(t: unknown): boolean {
  if (!t || typeof t !== "object") {
    return false;
  }
  const list = (t as { a?: unknown[] }).a;
  if (!Array.isArray(list)) {
    return false;
  }
  return list.some((anim) => {
    if (!anim || typeof anim !== "object") {
      return false;
    }
    const bag = (anim as { a?: { fc?: unknown } }).a;
    return bag != null && typeof bag === "object" && bag.fc != null;
  });
}

function applyTripleToAnimatorFcRoot(fcRoot: unknown, triple: [number, number, number]): void {
  if (fcRoot === undefined || fcRoot === null || typeof fcRoot !== "object") {
    return;
  }
  const fo = fcRoot as { a?: number; k?: unknown };
  if (fo.a === 0) {
    fo.k = [triple[0], triple[1], triple[2]];
    return;
  }
  if (fo.a !== 1 || !Array.isArray(fo.k)) {
    return;
  }
  for (const kf of fo.k) {
    if (!kf || typeof kf !== "object") {
      continue;
    }
    const ks = (kf as { s?: unknown }).s;
    if (Array.isArray(ks) && ks.length >= 3) {
      (kf as { s: [number, number, number] }).s = [triple[0], triple[1], triple[2]];
    }
  }
}

function syncTextAnimatorFillColour(layer: Record<string, unknown>, triple: [number, number, number]): void {
  const t = (layer as { t?: unknown }).t;
  if (!t || typeof t !== "object") {
    return;
  }
  const list = (t as { a?: unknown[] }).a;
  if (!Array.isArray(list)) {
    return;
  }
  for (const anim of list) {
    if (!anim || typeof anim !== "object") {
      continue;
    }
    const bag = (anim as { a?: { fc?: unknown } }).a;
    if (!bag || typeof bag !== "object") {
      continue;
    }
    applyTripleToAnimatorFcRoot((bag as { fc?: unknown }).fc, triple);
  }
}

function ensureTextAnimatorFillForRender(layer: Record<string, unknown>): boolean {
  if (getLayerTy(layer) !== 5) {
    return false;
  }
  const t = (layer as { t?: unknown }).t;
  if (!t || typeof t !== "object") {
    return false;
  }
  if (textAnimatorHasFillAnimator(t)) {
    return false;
  }
  const td = t as { a?: unknown[] };
  const animators = Array.isArray(td.a) ? td.a : [];
  const rgb = readTextFillRgb01(layer) ?? DEFAULT_TEXT_FILL_RGB01;
  const minimal = {
    nm: "",
    s: {
      t: 0,
      xe: { k: 0 },
      ne: { k: 0 },
      a: { a: 0, k: 0 },
      b: 1,
      rn: 0,
      sh: 1,
      r: 1,
      o: { k: 100 },
      e: { k: 100 },
    },
    a: {
      fc: { a: 0, k: [rgb[0], rgb[1], rgb[2]] },
    },
  };
  td.a = [...animators, minimal];
  return true;
}

function repairTextLayerIfNeeded(layer: Record<string, unknown>): boolean {
  if (getLayerTy(layer) !== 5) {
    return false;
  }
  if (readTextFillRgb01(layer) !== null) {
    return false;
  }
  applyTextFillToLayer(layer, DEFAULT_TEXT_FILL_RGB01);
  if (readTextFillRgb01(layer) !== null) {
    return true;
  }
  const wrap = getTextDocumentWrap(layer);
  if (wrap !== null) {
    coerceTextDocWrapDefaultFill(wrap);
    if (readTextFillRgb01(layer) !== null) {
      return true;
    }
  }
  attachMinimalTextDocument(layer);
  return readTextFillRgb01(layer) !== null;
}

function repairLayersArray(layers: unknown[]): number {
  let count = 0;
  for (const layer of layers) {
    if (!layer || typeof layer !== "object") {
      continue;
    }
    if (getLayerTy(layer) !== 5) {
      continue;
    }
    const L = layer as Record<string, unknown>;
    normaliseTextDocumentFcByteToUnit(L);
    if (repairTextLayerIfNeeded(L)) {
      count++;
    }
    ensureTextAnimatorFillForRender(L);
  }
  return count;
}

const JITTER_WATERMARK_REF_IDS = new Set(["8", "16"]);

interface LayerIndRef {
  ind?: unknown;
  parent?: unknown;
  refId?: unknown;
  ty?: unknown;
}

interface AssetWithLayers {
  id?: unknown;
  layers?: unknown[];
}

function buildAssetsById(assets: unknown[]): Map<string, AssetWithLayers> {
  const map = new Map<string, AssetWithLayers>();
  for (const raw of assets) {
    if (!raw || typeof raw !== "object") {
      continue;
    }
    const a = raw as AssetWithLayers;
    const id = a.id;
    if (typeof id === "string" && id.length > 0) {
      map.set(id, a);
    }
  }
  return map;
}

function collectReferencedAssetIds(
  layerArr: unknown[],
  assetsById: Map<string, AssetWithLayers>,
  out: Set<string>
): void {
  for (const raw of layerArr) {
    if (!raw || typeof raw !== "object") {
      continue;
    }
    const L = raw as LayerIndRef;
    if (typeof L.refId !== "string") {
      continue;
    }
    if (out.has(L.refId)) {
      continue;
    }
    out.add(L.refId);
    const asset = assetsById.get(L.refId);
    const nested = asset?.layers;
    if (Array.isArray(nested)) {
      collectReferencedAssetIds(nested, assetsById, out);
    }
  }
}

function tryAddNullParentsWithAllChildrenRemoved(layers: LayerIndRef[], toRemove: Set<number>): boolean {
  let added = false;
  for (const L of layers) {
    const ind = L.ind;
    if (typeof ind !== "number" || toRemove.has(ind)) {
      continue;
    }
    const children = layers.filter((x) => x.parent === ind);
    if (children.length === 0) {
      continue;
    }
    if (children.every((c) => typeof c.ind === "number" && toRemove.has(c.ind))) {
      toRemove.add(ind);
      added = true;
    }
  }
  return added;
}

function collectJitterWatermarkRemovalIndices(layers: LayerIndRef[]): Set<number> {
  const toRemove = new Set<number>();
  for (const L of layers) {
    if (typeof L.refId === "string" && JITTER_WATERMARK_REF_IDS.has(L.refId) && typeof L.ind === "number") {
      toRemove.add(L.ind);
    }
  }
  while (tryAddNullParentsWithAllChildrenRemoved(layers, toRemove)) {
    // peel wrapper nulls until stable
  }
  return toRemove;
}

export interface StripJitterWatermarkResult {
  data: unknown;
  removedLayerCount: number;
  removedAssetCount: number;
  metaCleared: boolean;
}

interface LottieMeta {
  g?: unknown;
  [key: string]: unknown;
}

interface LottieRootStripShape {
  layers?: unknown;
  assets?: unknown;
  meta?: LottieMeta;
}

/**
 * Removes common Jitter export badge precomps (refId 8 / 16), orphan null wrappers, unreferenced assets, and meta.g.
 * Does not remove in-scene vector text; only embedded precomp watermarks.
 */
export function stripJitterWatermarkFromLottieJson(root: unknown): StripJitterWatermarkResult {
  if (!root || typeof root !== "object") {
    return { data: root, removedLayerCount: 0, removedAssetCount: 0, metaCleared: false };
  }
  const clone = JSON.parse(JSON.stringify(root)) as LottieRootStripShape;
  const layersRaw = clone.layers;
  if (!Array.isArray(layersRaw)) {
    return { data: clone, removedLayerCount: 0, removedAssetCount: 0, metaCleared: false };
  }
  const layerList = layersRaw as LayerIndRef[];
  const toRemove = collectJitterWatermarkRemovalIndices(layerList);
  const beforeLayerCount = layerList.length;
  const filtered = layerList.filter((L) => {
    if (typeof L.ind === "number") {
      return !toRemove.has(L.ind);
    }
    return true;
  });
  clone.layers = filtered;
  const removedLayerCount = beforeLayerCount - filtered.length;

  let removedAssetCount = 0;
  const assetsRaw = clone.assets;
  if (Array.isArray(assetsRaw) && assetsRaw.length > 0) {
    const beforeAssetCount = assetsRaw.length;
    const assetsById = buildAssetsById(assetsRaw as unknown[]);
    const used = new Set<string>();
    collectReferencedAssetIds(filtered as unknown[], assetsById, used);
    const nextAssets = (assetsRaw as unknown[]).filter((a) => {
      if (!a || typeof a !== "object") {
        return false;
      }
      const id = (a as AssetWithLayers).id;
      return typeof id === "string" && used.has(id);
    });
    clone.assets = nextAssets;
    removedAssetCount = beforeAssetCount - nextAssets.length;
  }

  let metaCleared = false;
  const meta = clone.meta;
  if (meta && typeof meta === "object" && "g" in meta) {
    delete meta.g;
    metaCleared = true;
  }

  return { data: clone, removedLayerCount, removedAssetCount, metaCleared };
}

export function normalizeLottiePlaygroundJson(root: unknown): {
  data: unknown;
  repairedTextFillCount: number;
} {
  if (!root || typeof root !== "object") {
    return { data: root, repairedTextFillCount: 0 };
  }
  const clone = JSON.parse(JSON.stringify(root)) as LottieJsonClone;
  let repairedTextFillCount = 0;
  const visit = (layers: unknown[]) => {
    repairedTextFillCount += repairLayersArray(layers);
  };
  if (Array.isArray(clone.layers)) {
    visit(clone.layers);
  }
  if (Array.isArray(clone.assets)) {
    for (const asset of clone.assets) {
      if (asset && typeof asset === "object") {
        const al = (asset as AssetMaybeLayers).layers;
        if (Array.isArray(al)) {
          visit(al);
        }
      }
    }
  }
  return { data: clone, repairedTextFillCount };
}

export function setLayerTextFillColour(root: unknown, layerIndex: number, rgb: [number, number, number]): unknown {
  const next = JSON.parse(JSON.stringify(root));
  const layers = (next as RootLike).layers;
  if (!Array.isArray(layers)) {
    return root;
  }
  const layer = layers[layerIndex];
  if (!layer || typeof layer !== "object") {
    return root;
  }
  if (getLayerTy(layer) !== 5) {
    return root;
  }
  if (!applyTextFillToLayer(layer as Record<string, unknown>, rgb)) {
    return root;
  }
  syncTextAnimatorFillColour(layer as Record<string, unknown>, rgb);
  ensureTextAnimatorFillForRender(layer as Record<string, unknown>);
  return next;
}

export function getTextDocumentK(layer: unknown): unknown {
  if (!layer || typeof layer !== "object") {
    return undefined;
  }
  return (layer as LayerTextNav).t?.d?.k;
}

export function readFillRgb01(cObj: unknown): [number, number, number] | null {
  if (!cObj || typeof cObj !== "object") {
    return null;
  }
  const c = cObj as { a?: number; k?: unknown };
  if (c.a === 0) {
    return rgbTripleFromArray(c.k);
  }
  if (c.a !== 1 || !Array.isArray(c.k) || c.k.length === 0) {
    return null;
  }
  const k0 = c.k[0];
  if (!k0 || typeof k0 !== "object" || !("s" in k0)) {
    return null;
  }
  return rgbTripleFromArray((k0 as { s?: unknown }).s);
}

export function staticColourProperty(rgb: [number, number, number]): { a: 0; k: [number, number, number] } {
  return { a: 0, k: rgb };
}

export function listLayerTransformKeys(ks: unknown): { key: string; label: string }[] {
  if (!ks || typeof ks !== "object") {
    return [];
  }
  const o = ks as Record<string, unknown>;
  const out: { key: string; label: string }[] = [];
  for (const key of Object.keys(o)) {
    if (key === "a") {
      continue;
    }
    out.push({ key, label: KS_LABELS[key] ?? key });
  }
  return out;
}
